/**
 * Responsive application shell (CLAUDE.md 14.3).
 *
 * Four layouts, one component tree:
 *
 *   >= 1280 (xl)   three panes: project tree | inputs | live results (FRILO-like),
 *                  with a draggable, persisted split between inputs and results
 *   1024-1279 (lg) two panes: the tree collapses to an icon rail
 *   768-1023 (md)  the tree becomes a drawer; inputs and results become tabs, with a
 *                  persistent status banner so the headline utilisation stays visible
 *                  while editing
 *   < 768          single column, bottom tab bar: Inputs / Results / Report
 *
 * Three defects in the previous version are fixed here, and each is worth naming
 * because each was invisible until someone tried the thing:
 *
 * 1. **The Report pane was unreachable above 768px.** The bottom tab bar was the only
 *    control that could set the active pane, and it was `md:hidden`. So on every
 *    tablet and desktop the Report panel could never be shown. The pane selector now
 *    exists at every size -- as tabs on tablet and above, as the bottom bar below.
 * 2. **The tablet breakpoint was wrong.** The split started at `md` (768), giving a
 *    ~320px input column, when 14.3 specifies tabs until `lg` (1024).
 * 3. **The desktop split was neither resizable nor persisted**, though 14.3 requires
 *    both. In a tool where the engineer stares at one pane for an hour, this is the
 *    single most-used ergonomic control.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import * as Tabs from '@radix-ui/react-tabs'
import { FileText, FolderTree, Menu, SlidersHorizontal, Table2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { Dialog, DialogTitle, DrawerContent, DialogTrigger } from '@/components/ui/dialog'

export type Pane = 'inputs' | 'results' | 'report'

const SPLIT_STORAGE_KEY = 'calcdesk.split'
const MIN_SPLIT = 25
const MAX_SPLIT = 70

interface AppShellProps {
  tree?: ReactNode
  inputs?: ReactNode
  results?: ReactNode
  report?: ReactNode
  /** Kept visible below `xl`, so the headline is never lost while editing. */
  statusBanner?: ReactNode
  /** Page-level identity: module, version, code. Rendered above the panes. */
  header?: ReactNode
  title?: string
  /** Global chrome: workspace switcher, user menu, theme and language. */
  toolbar?: ReactNode
}

function readSplit(): number {
  try {
    const stored = Number(localStorage.getItem(SPLIT_STORAGE_KEY))
    if (Number.isFinite(stored) && stored >= MIN_SPLIT && stored <= MAX_SPLIT) return stored
  } catch {
    // Storage unavailable; the default split is fine.
  }
  return 42
}

export function AppShell({
  tree,
  inputs,
  results,
  report,
  statusBanner,
  header,
  title,
  toolbar,
}: AppShellProps) {
  const { t } = useTranslation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [pane, setPane] = useState<Pane>('inputs')
  const [split, setSplit] = useState(readSplit)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef(false)

  const panes: { id: Pane; labelKey: string; icon: typeof SlidersHorizontal }[] = [
    { id: 'inputs', labelKey: 'nav.inputs', icon: SlidersHorizontal },
    { id: 'results', labelKey: 'nav.results', icon: Table2 },
    { id: 'report', labelKey: 'nav.report', icon: FileText },
  ]

  /* --- resizable split (xl only) ------------------------------------------------ */

  const onDrag = useCallback((event: PointerEvent) => {
    if (!dragging.current || !containerRef.current) return
    const bounds = containerRef.current.getBoundingClientRect()
    const percent = ((event.clientX - bounds.left) / bounds.width) * 100
    setSplit(Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, percent)))
  }, [])

  const endDrag = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    document.body.style.userSelect = ''
    setSplit((current) => {
      try {
        localStorage.setItem(SPLIT_STORAGE_KEY, String(Math.round(current)))
      } catch {
        // Non-fatal: the split simply will not survive a reload.
      }
      return current
    })
  }, [])

  useEffect(() => {
    window.addEventListener('pointermove', onDrag)
    window.addEventListener('pointerup', endDrag)
    return () => {
      window.removeEventListener('pointermove', onDrag)
      window.removeEventListener('pointerup', endDrag)
    }
  }, [onDrag, endDrag])

  const paneContent: Record<Pane, ReactNode> = { inputs, results, report }

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface-raised focus:px-3 focus:py-2"
      >
        {t('nav.skip_to_content')}
      </a>

      {/* --- header ---------------------------------------------------------- */}
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-2 sm:px-4">
        {tree && (
          <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DialogTrigger
              aria-label={t('nav.projects')}
              className="flex h-11 w-11 items-center justify-center rounded-md text-text-muted hover:bg-surface-raised hover:text-text xl:hidden"
            >
              <Menu size={20} aria-hidden />
            </DialogTrigger>
            {/* Radix supplies the focus trap, Escape handling, scroll lock and
                focus restoration the hand-rolled drawer was missing. */}
            <DrawerContent>
              <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
                <FolderTree size={16} aria-hidden />
                <DialogTitle className="text-sm font-semibold">{t('nav.projects')}</DialogTitle>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">{tree}</div>
            </DrawerContent>
          </Dialog>
        )}

        <span className="truncate font-semibold">{title ?? t('app.name')}</span>

        <div className="ms-auto flex items-center gap-1">{toolbar}</div>
      </header>

      {/* --- identity + status ------------------------------------------------ */}
      {header && (
        <div className="shrink-0 border-b border-border bg-surface px-3 py-2 sm:px-4">{header}</div>
      )}
      {statusBanner && (
        <div className="shrink-0 border-b border-border bg-surface px-3 py-2 sm:px-4 lg:hidden">
          {statusBanner}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* Desktop tree (>= 1280) */}
        {tree && (
          <aside className="hidden w-72 shrink-0 overflow-y-auto border-e border-border bg-surface xl:block">
            {tree}
          </aside>
        )}

        <main id="main" className="flex min-w-0 flex-1 flex-col">
          {/* Tabs from lg upward select which of results/report sits beside the
              inputs; below lg they select the single visible pane. Either way the
              Report pane is always reachable. */}
          <Tabs.Root
            value={pane}
            onValueChange={(value) => setPane(value as Pane)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <Tabs.List
              className="hidden shrink-0 gap-1 border-b border-border bg-surface px-2 md:flex"
              aria-label={t('nav.panes')}
            >
              {panes.map(({ id, labelKey, icon: Icon }) => (
                <Tabs.Trigger
                  key={id}
                  value={id}
                  className={cn(
                    'flex items-center gap-2 border-b-2 border-transparent px-3 py-2.5 text-sm',
                    'text-text-muted hover:text-text',
                    'data-[state=active]:border-accent data-[state=active]:text-text',
                    // From lg the inputs pane is always on screen, so selecting it
                    // as a tab would be meaningless.
                    id === 'inputs' && 'lg:hidden',
                  )}
                >
                  <Icon size={15} aria-hidden />
                  {t(labelKey)}
                </Tabs.Trigger>
              ))}
            </Tabs.List>

            <div ref={containerRef} className="flex min-h-0 flex-1 lg:flex-row">
              {/* Inputs: always visible at xl, tab-selected below. */}
              <section
                className={cn(
                  'min-w-0 overflow-y-auto p-3 sm:p-4',
                  'lg:block lg:shrink-0 lg:border-e lg:border-border',
                  pane === 'inputs' ? 'block w-full' : 'hidden',
                )}
                style={{ flexBasis: `${split}%` }}
                aria-label={t('nav.inputs')}
              >
                {inputs}
              </section>

              {/* Drag handle. Pointer-only by nature, so it is also keyboard
                  operable and exposed as a separator with a value. */}
              {/*
                The WAI-ARIA window-splitter pattern is exactly this: role="separator"
                with an orientation, a value, and a tab stop so it is keyboard
                operable. The two rules below assume "separator" means a decorative
                rule, which is the other, non-focusable use of the same role.
              */}
              {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t('nav.resize_panes')}
                aria-valuenow={Math.round(split)}
                aria-valuemin={MIN_SPLIT}
                aria-valuemax={MAX_SPLIT}
                tabIndex={0}
                onPointerDown={() => {
                  dragging.current = true
                  // Stops the drag selecting text across both panes.
                  document.body.style.userSelect = 'none'
                }}
                onKeyDown={(event) => {
                  const step = event.shiftKey ? 5 : 1
                  if (event.key === 'ArrowLeft') {
                    event.preventDefault()
                    setSplit((s) => Math.max(MIN_SPLIT, s - step))
                  } else if (event.key === 'ArrowRight') {
                    event.preventDefault()
                    setSplit((s) => Math.min(MAX_SPLIT, s + step))
                  } else {
                    return
                  }
                  endDrag()
                }}
                className="hidden w-1 shrink-0 cursor-col-resize bg-border hover:bg-accent focus-visible:bg-accent lg:block"
              />
              {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}

              <section
                className={cn(
                  'min-w-0 flex-1 overflow-y-auto p-3 sm:p-4',
                  pane === 'inputs' ? 'hidden lg:block' : 'block',
                )}
                aria-label={t(pane === 'report' ? 'nav.report' : 'nav.results')}
              >
                {/* From lg the inputs tab is hidden, so "inputs" means "show results". */}
                {pane === 'inputs' ? results : paneContent[pane]}
              </section>
            </div>
          </Tabs.Root>
        </main>
      </div>

      {/* Mobile bottom tab bar (< 768). Review-first navigation (14.3). */}
      <nav
        className="sticky bottom-0 z-30 flex shrink-0 border-t border-border bg-surface md:hidden"
        aria-label={t('nav.panes')}
      >
        {panes.map(({ id, labelKey, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setPane(id)}
            aria-current={pane === id ? 'page' : undefined}
            className={cn(
              'flex flex-1 flex-col items-center gap-0.5 py-2 text-xs',
              // 44px minimum touch target, plus safe-area padding for the home bar.
              'min-h-[3.25rem] pb-[max(0.5rem,env(safe-area-inset-bottom))]',
              pane === id ? 'text-accent' : 'text-text-muted',
            )}
          >
            <Icon size={18} aria-hidden />
            {t(labelKey)}
          </button>
        ))}
      </nav>
    </div>
  )
}
